/**
 * InspectorModule — Inspector de objetos web para construcción de selectores.
 */
import React, { useState, useCallback, useEffect } from 'react';
import { agentsApi } from '../../services/api';
import type { Agent } from '../../types';

// ─── API helper ───────────────────────────────────────────────────────────────

const BASE = '/api/v1';

async function apiGet<T>(path: string): Promise<T> {
  const token = localStorage.getItem('access_token') || '';
  const res = await fetch(`${BASE}${path}`, { headers: { 'Authorization': `Bearer ${token}` } });
  if (!res.ok) throw new Error(`Error ${res.status}`);
  return res.json() as Promise<T>;
}

async function apiPost<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const token = localStorage.getItem('access_token') || '';
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as Record<string, string>;
    throw new Error(err['detail'] || `Error ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ─── Scraper del agente LOCAL ────────────────────────────────────────────────
// El Inspector usa el servidor scraper que corre en el agente del usuario (misma
// máquina que el navegador). Se llama DIRECTO a localhost:8001, NO por el backend:
// cuando la plataforma está en la nube, el backend no puede alcanzar el agente
// local del usuario. El scraper del agente ya expone CORS; los navegadores
// permiten https://panel → http://localhost (loopback de confianza).
const SCRAPER_BASE = 'http://localhost:8001';

async function scraperPost<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const res = await fetch(`${SCRAPER_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as Record<string, string>;
    throw new Error(err['detail'] || `Error ${res.status}`);
  }
  return res.json() as Promise<T>;
}

async function scraperGet<T>(path: string): Promise<T> {
  const res = await fetch(`${SCRAPER_BASE}${path}`);
  if (!res.ok) throw new Error(`Error ${res.status}`);
  return res.json() as Promise<T>;
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface SelectorSuggestion {
  strategy: 'css' | 'xpath' | 'text' | 'testid' | 'role';
  selector: string;
  confidence: 'alta' | 'media' | 'baja';
}

interface ElementProps {
  index: number;
  tag: string;
  id?: string;
  name?: string;
  type?: string;
  class?: string;
  text?: string;
  placeholder?: string;
  href?: string;
  src?: string;
  alt?: string;
  aria_label?: string;
  data_testid?: string;
  role?: string;
  visible?: boolean;
  enabled?: boolean;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  suggestions: SelectorSuggestion[];
  error?: string;
}

interface InspectResult {
  ok: boolean;
  url?: string;
  title?: string;
  element_type?: string;
  total_found?: number;
  returned?: number;
  elements: ElementProps[];
  error?: string;
  browser_open?: boolean;
}

interface ValidateResult {
  ok: boolean;
  found: boolean;
  count: number;
  selector?: string;
  strategy?: string;
  elements: ElementProps[];
  error?: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const ELEMENT_TYPES = [
  { value: 'all',      label: 'Todos (con ID/testid/name)',  icon: '◉' },
  { value: 'buttons',  label: 'Botones',                     icon: '⊡' },
  { value: 'inputs',   label: 'Inputs / Formularios',        icon: '▭' },
  { value: 'links',    label: 'Links',                       icon: '⇗' },
  { value: 'images',   label: 'Imágenes',                    icon: '⬚' },
  { value: 'tables',   label: 'Tablas',                      icon: '⊞' },
  { value: 'headings', label: 'Encabezados',                 icon: 'H' },
  { value: 'labeled',  label: 'Todos con identificadores',   icon: '◈' },
];

const STRATEGIES = [
  { value: 'css',    label: 'CSS' },
  { value: 'xpath',  label: 'XPath' },
  { value: 'text',   label: 'Texto' },
  { value: 'testid', label: 'data-testid' },
  { value: 'role',   label: 'ARIA role' },
];

function isValidUrl(v: string): boolean {
  return /^https?:\/\//i.test(v.trim());
}

const CONFIDENCE_CFG: Record<string, { color: string; bg: string }> = {
  alta:  { color: '#0F6E56', bg: '#E1F5EE' },
  media: { color: '#854F0B', bg: '#FAEEDA' },
  baja:  { color: '#5F5E5A', bg: '#F1EFE8' },
};

const STRATEGY_COLOR: Record<string, string> = {
  css: '#185FA5', xpath: '#534AB7', text: '#0F6E56',
  testid: '#854F0B', role: '#5F3B00',
};

// ─── Shared UI ────────────────────────────────────────────────────────────────

const INPUT: React.CSSProperties = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: '1px solid var(--color-border-secondary)',
  background: 'var(--color-background-secondary)',
  color: 'var(--color-text-primary)', fontSize: 13,
  boxSizing: 'border-box', outline: 'none',
};

function CopyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid var(--color-border-secondary)', background: copied ? '#E1F5EE' : 'var(--color-background-primary)', cursor: 'pointer', fontSize: 10, color: copied ? '#0F6E56' : 'var(--color-text-tertiary)', whiteSpace: 'nowrap' }}>
      {copied ? '✓ Copiado' : '📋 Copiar'}
    </button>
  );
}

// ─── Element Card ─────────────────────────────────────────────────────────────

function ElementCard({ el, index, highlighted, onHighlight }: { el: ElementProps; index: number; highlighted: boolean; onHighlight: () => void }) {
  const [expanded, setExpanded] = useState(false);

  const mainProps = ([
    el.id && { key: 'id', val: el.id },
    el.name && { key: 'name', val: el.name },
    el.type && { key: 'type', val: el.type },
    el.text && { key: 'texto', val: el.text.slice(0, 60) },
    el.placeholder && { key: 'placeholder', val: el.placeholder },
    el.aria_label && { key: 'aria-label', val: el.aria_label },
    el.data_testid && { key: 'data-testid', val: el.data_testid },
    el.href && { key: 'href', val: el.href.slice(0, 60) },
    el.alt && { key: 'alt', val: el.alt },
  ].filter(Boolean)) as { key: string; val: string }[];

  const top = el.suggestions?.[0];

  return (
    <div style={{ border: '1px solid var(--color-border-tertiary)', borderRadius: 10, overflow: 'hidden', marginBottom: 8 }}>
      <div
        onClick={() => { setExpanded(v => !v); onHighlight(); }}
        style={{ padding: '10px 14px', background: highlighted ? '#FFF0E6' : 'var(--color-background-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, borderLeft: highlighted ? '3px solid #F47920' : '3px solid transparent', transition: 'all 0.15s' }}>
        <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)', minWidth: 24 }}>#{index + 1}</span>
        <code style={{ padding: '2px 7px', borderRadius: 5, background: '#1B2E5E', color: '#B5D4F4', fontSize: 11, fontWeight: 700 }}>
          &lt;{el.tag}&gt;
        </code>
        {el.id && <span style={{ fontSize: 11, color: '#185FA5', fontFamily: 'monospace' }}>#{el.id}</span>}
        {!el.id && el.name && <span style={{ fontSize: 11, color: '#534AB7', fontFamily: 'monospace' }}>[name="{el.name}"]</span>}
        {el.text && !el.id && !el.name && <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>"{el.text.slice(0, 40)}"</span>}
        <div style={{ flex: 1 }} />
        {el.visible === false && <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, background: '#F1EFE8', color: '#5F5E5A' }}>oculto</span>}
        {top && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <code style={{ fontSize: 10, color: 'var(--color-text-tertiary)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{top.selector}</code>
            <CopyBtn text={top.selector} />
          </div>
        )}
        <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div style={{ padding: '12px 14px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', marginBottom: 14 }}>
            {mainProps.map(p => (
              <div key={p.key} style={{ display: 'flex', gap: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', minWidth: 70, flexShrink: 0 }}>{p.key}</span>
                <span style={{ fontSize: 12, color: 'var(--color-text-primary)', fontFamily: 'monospace', wordBreak: 'break-all' }}>{p.val}</span>
              </div>
            ))}
            {el.class && (
              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', minWidth: 70 }}>class</span>
                <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', fontFamily: 'monospace', wordBreak: 'break-all' }}>{el.class}</span>
              </div>
            )}
            {el.x !== undefined && (
              <div style={{ display: 'flex', gap: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', minWidth: 70 }}>posición</span>
                <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{el.x},{el.y} — {el.width}×{el.height}px</span>
              </div>
            )}
          </div>

          {(el.suggestions?.length ?? 0) > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Selectores sugeridos
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {el.suggestions.map((s, i) => {
                  const cc = CONFIDENCE_CFG[s.confidence] ?? CONFIDENCE_CFG['baja'];
                  const sc = STRATEGY_COLOR[s.strategy] ?? '#185FA5';
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 7, background: 'var(--color-background-secondary)', border: '1px solid var(--color-border-tertiary)' }}>
                      <span style={{ padding: '1px 6px', borderRadius: 4, background: sc + '22', color: sc, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', minWidth: 42, textAlign: 'center' as const }}>
                        {s.strategy}
                      </span>
                      <code style={{ flex: 1, fontSize: 12, fontFamily: 'monospace', color: 'var(--color-text-primary)', wordBreak: 'break-all' as const }}>
                        {s.selector}
                      </code>
                      <span style={{ padding: '1px 6px', borderRadius: 4, background: cc.bg, color: cc.color, fontSize: 9, fontWeight: 600, flexShrink: 0 }}>
                        {s.confidence}
                      </span>
                      <CopyBtn text={s.selector} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Agent Status ─────────────────────────────────────────────────────────────

const STALE_MS = 2 * 60 * 1000;

function resolveStatus(agent: Pick<Agent, 'status' | 'last_heartbeat'>): string {
  const stale = agent.last_heartbeat
    ? Date.now() - new Date(agent.last_heartbeat).getTime() > STALE_MS
    : false;
  return stale && agent.status !== 'busy' ? 'offline' : agent.status;
}

function timeAgo(iso?: string | null): string {
  if (!iso) return '';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `hace ${diff}s`;
  if (diff < 3600) return `hace ${Math.floor(diff / 60)}min`;
  return `hace ${Math.floor(diff / 3600)}h`;
}

function AgentStatusBanner({ agents }: { agents: Agent[] }) {
  const active = agents.filter(a => resolveStatus(a) !== 'offline');
  const busy   = active.filter(a => resolveStatus(a) === 'busy');

  if (agents.length === 0) {
    return (
      <div style={{ padding: '10px 14px', borderRadius: 8, background: '#FFF8E1', border: '1px solid #FFD54F', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#FFA000', flexShrink: 0 }} />
        <div>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#E65100' }}>Sin agentes registrados</span>
          <span style={{ fontSize: 11, color: '#BF360C', marginLeft: 8 }}>El inspector requiere un agente activo — ejecuta <code>python src/main.py</code></span>
        </div>
      </div>
    );
  }

  if (active.length === 0) {
    return (
      <div style={{ padding: '10px 14px', borderRadius: 8, background: '#FCEBEB', border: '1px solid #F7C1C1', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#C62828', flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#A32D2D' }}>Agente offline</span>
          <span style={{ fontSize: 11, color: '#A32D2D', marginLeft: 8 }}>Ningún agente disponible — el inspector no podrá ejecutar</span>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {agents.map(a => (
            <span key={a.id} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: '#F7C1C1', color: '#A32D2D', fontWeight: 600 }}>
              ● offline · {a.name} · {timeAgo(a.last_heartbeat)}
            </span>
          ))}
        </div>
      </div>
    );
  }

  const dotColor = busy.length > 0 ? '#185FA5' : '#0F6E56';
  const bgColor  = busy.length > 0 ? '#E6F1FB' : '#E1F5EE';
  const bdColor  = busy.length > 0 ? '#B5D4F4' : '#9DE3C7';
  const label    = busy.length > 0 ? 'Agente ocupado' : 'Agente activo';
  const best     = busy.length > 0 ? busy[0] : active[0];

  return (
    <div style={{ padding: '10px 14px', borderRadius: 8, background: bgColor, border: `1px solid ${bdColor}`, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0,
        boxShadow: busy.length > 0 ? `0 0 0 3px ${dotColor}33` : `0 0 0 2px ${dotColor}44` }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: dotColor }}>{label}</span>
        <span style={{ fontSize: 11, color: dotColor, marginLeft: 8 }}>
          {best.name} · ID: {best.id.slice(0, 8).toUpperCase()}
          {timeAgo(best.last_heartbeat) && <span style={{ marginLeft: 6, opacity: 0.7 }}>{timeAgo(best.last_heartbeat)}</span>}
        </span>
      </div>
      {active.length > 1 && (
        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: dotColor + '22', color: dotColor, fontWeight: 600 }}>
          +{active.length - 1} más
        </span>
      )}
    </div>
  );
}

// ─── Main Module ─────────────────────────────────────────────────────────────

export function InspectorModule() {
  const [agents, setAgents] = useState<Agent[]>([]);

  useEffect(() => {
    const load = () => agentsApi.list().then(setAgents).catch(() => {});
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, []);

  const [mode, setMode] = useState<'inspect' | 'validate'>('inspect');

  const [url, setUrl] = useState('');
  const [elementType, setElementType] = useState('all');
  const [inspecting, setInspecting] = useState(false);
  const [inspectResult, setInspectResult] = useState<InspectResult | null>(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('all');

  const [validateUrl, setValidateUrl] = useState('');
  const [selector, setSelector] = useState('');
  const [strategy, setStrategy] = useState('css');
  const [validating, setValidating] = useState(false);
  const [validateResult, setValidateResult] = useState<ValidateResult | null>(null);

  const [error, setError] = useState('');
  const [highlightedIdx, setHighlightedIdx] = useState<number | null>(null);
  const [currentUrl, setCurrentUrl] = useState('');
  const [browserOpen, setBrowserOpen] = useState(false);

  // ── POM Import state ─────────────────────────────────────────────────────
  const [showImportPanel, setShowImportPanel] = useState(false);
  const [selectedForImport, setSelectedForImport] = useState<Set<number>>(new Set());
  const [pomPages, setPomPages] = useState<{system_id: string; system_name: string; pages: {id: string; name: string; url_pattern?: string}[]}[]>([]);
  const [loadingPages, setLoadingPages] = useState(false);
  const [selectedPageId, setSelectedPageId] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{created: number; skipped: number} | null>(null);

  const loadPomPages = useCallback(async () => {
    setLoadingPages(true);
    try {
      const data = await apiGet<typeof pomPages>('/pages/by-tenant');
      setPomPages(data);
      // Auto-select first page if available
      const first = data[0]?.pages[0];
      if (first) setSelectedPageId(first.id);
    } catch { }
    finally { setLoadingPages(false); }
  }, []);

  const toggleSelectForImport = (idx: number) => {
    setSelectedForImport(prev => {
      const n = new Set(prev);
      n.has(idx) ? n.delete(idx) : n.add(idx);
      return n;
    });
  };

  const selectAllVisible = () => {
    setSelectedForImport(new Set(filtered.map(el => el.index)));
  };

  const handleImportToPOM = async () => {
    if (!selectedPageId || selectedForImport.size === 0) return;
    setImporting(true);
    setImportResult(null);
    try {
      const toImport = filtered
        .filter(el => selectedForImport.has(el.index))
        .map(el => {
          const top = el.suggestions?.[0];
          const name = el.id ? `#${el.id}`
            : el.name ? el.name
            : el.aria_label ? el.aria_label
            : el.text?.slice(0, 30) || `${el.tag}-${el.index}`;
          return {
            name,
            selector_primary: top?.selector ?? (el.id ? `#${el.id}` : el.tag),
            selector_fallbacks: (el.suggestions?.slice(1) ?? []).map(s => s.selector),
            selector_strategy: top?.strategy ?? 'css',
            element_type: el.tag === 'button' ? 'button'
              : el.tag === 'input' ? 'input'
              : el.tag === 'a' ? 'link'
              : el.tag === 'select' ? 'select'
              : el.tag === 'img' ? 'image'
              : 'element',
            description: el.text ? el.text.slice(0, 100) : undefined,
          };
        });

      const result = await apiPost<{created: number; skipped: number}>(
        `/pages/${selectedPageId}/objects/import`,
        { objects: toImport, skip_duplicates: true }
      );
      setImportResult(result);
      setSelectedForImport(new Set());
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setImporting(false);
    }
  };

  const handleHighlight = useCallback(async (el: ElementProps, inspectUrl: string) => {
    const topSuggestion = el.suggestions?.[0];
    if (!topSuggestion) return;
    setHighlightedIdx(el.index);
    try {
      await scraperPost('/scraper/highlight', {
        url: inspectUrl,
        selector: topSuggestion.selector,
        strategy: topSuggestion.strategy,
      });
    } catch (e) {
      console.warn('highlight failed:', e);
    }
  }, []);

  const handleClearHighlight = useCallback(async () => {
    setHighlightedIdx(null);
    try { await scraperPost('/scraper/clear', {}); } catch { }
  }, []);

  const handleReInspect = useCallback(async () => {
    setInspecting(true);
    setError('');
    try {
      const status = await scraperGet<{ ok: boolean; browser_open: boolean; current_url: string }>('/scraper/status');
      if (!status.ok || !status.current_url) {
        setError('No se pudo obtener la URL actual del navegador.');
        return;
      }
      const newUrl = status.current_url;
      setUrl(newUrl);
      setInspectResult(null);
      setTagFilter('all');
      setSearchFilter('');
      const result = await scraperPost<InspectResult>('/scraper/inspect', { url: newUrl, element_type: elementType });
      setInspectResult(result);
      if (result.ok) {
        setCurrentUrl(newUrl);
        setBrowserOpen(result.browser_open ?? false);
      }
      if (!result.ok) setError(result.error ?? 'Error al inspeccionar');
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setInspecting(false);
    }
  }, [elementType]);

  const handleInspect = useCallback(async () => {
    if (!url.trim()) return;
    if (!isValidUrl(url)) { setError('La URL debe comenzar con http:// o https://'); return; }
    setInspecting(true); setError(''); setInspectResult(null); setTagFilter('all'); setSearchFilter('');
    try {
      const result = await scraperPost<InspectResult>('/scraper/inspect', { url: url.trim(), element_type: elementType });
      setInspectResult(result);
      if (result.ok) {
        setCurrentUrl(url.trim());
        setBrowserOpen(result.browser_open ?? false);
      }
      if (!result.ok) setError(result.error ?? 'Error al inspeccionar');
    } catch (e: unknown) { setError((e as Error).message); }
    finally { setInspecting(false); }
  }, [url, elementType]);

  const handleValidate = useCallback(async () => {
    if (!validateUrl.trim() || !selector.trim()) return;
    if (!isValidUrl(validateUrl)) { setError('La URL debe comenzar con http:// o https://'); return; }
    setValidating(true); setError(''); setValidateResult(null);
    try {
      const result = await scraperPost<ValidateResult>('/scraper/validate', { url: validateUrl.trim(), selector: selector.trim(), strategy });
      setValidateResult(result);
      if (!result.ok) setError(result.error ?? 'Error al validar');
    } catch (e: unknown) { setError((e as Error).message); }
    finally { setValidating(false); }
  }, [validateUrl, selector, strategy]);

  // Available tags from current results for the sub-filter
  const availableTags = Array.from(new Set((inspectResult?.elements ?? []).map(el => el.tag))).sort();

  const filtered = (inspectResult?.elements ?? []).filter(el => {
    if (tagFilter !== 'all' && el.tag !== tagFilter) return false;
    if (!searchFilter) return true;
    const f = searchFilter.toLowerCase();
    return [el.tag, el.id, el.name, el.text, el.class, el.placeholder, el.aria_label, el.data_testid]
      .some(v => v?.toLowerCase().includes(f));
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' }}>

      {/* Mode tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border-tertiary)', marginBottom: 16 }}>
        {[{ key: 'inspect', label: '🔍 Inspeccionar página' }, { key: 'validate', label: '✓ Validar selector' }].map(tab => (
          <button key={tab.key} onClick={() => { setMode(tab.key as 'inspect' | 'validate'); setError(''); }}
            style={{ padding: '12px 20px', border: 'none', borderBottom: `2px solid ${mode === tab.key ? '#3B8BD4' : 'transparent'}`, background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: mode === tab.key ? 700 : 400, color: mode === tab.key ? '#3B8BD4' : 'var(--color-text-secondary)' }}>
            {tab.label}
          </button>
        ))}
      </div>

      <AgentStatusBanner agents={agents} />

      {error && (
        <div style={{ padding: '10px 14px', borderRadius: 8, background: '#FCEBEB', border: '1px solid #F7C1C1', color: '#A32D2D', fontSize: 12, marginBottom: 14 }}>
          ⚠ {error}
        </div>
      )}

      {/* ── INSPECT ── */}
      {mode === 'inspect' && (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div style={{ display: 'flex', gap: 10, marginBottom: url.trim() && !isValidUrl(url) ? 4 : 14, flexWrap: 'wrap' }}>
            <input
              style={{ ...INPUT, flex: 2, minWidth: 240, border: url.trim() && !isValidUrl(url) ? '1px solid #F7C1C1' : INPUT.border }}
              value={url} onChange={e => { setUrl(e.target.value); setError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleInspect()} placeholder="https://ejemplo.com/login" />
            {url.trim() && !isValidUrl(url) && (
              <div style={{ width: '100%', fontSize: 11, color: '#A32D2D', marginTop: -6, paddingLeft: 2 }}>
                La URL debe incluir el protocolo · ej: <strong>https://ejemplo.com/login</strong>
              </div>
            )}
            <select style={{ ...INPUT, flex: 1, minWidth: 160 }} value={elementType} onChange={e => setElementType(e.target.value)}>
              {ELEMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
            </select>
            <button onClick={handleInspect} disabled={!isValidUrl(url) || inspecting}
              style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: isValidUrl(url) ? '#3B8BD4' : 'var(--color-background-secondary)', color: isValidUrl(url) ? '#fff' : 'var(--color-text-tertiary)', cursor: isValidUrl(url) ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
              {inspecting ? '⟳ Inspeccionando...' : '🔍 Inspeccionar'}
            </button>
          </div>

          {inspectResult?.ok && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{inspectResult.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{inspectResult.url}</div>
                </div>
                <span style={{ padding: '3px 10px', borderRadius: 6, background: '#E1F5EE', color: '#0F6E56', fontSize: 12, fontWeight: 600 }}>
                  {filtered.length} / {inspectResult.total_found} elementos
                </span>
                {browserOpen && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 6, background: '#E6F1FB', color: '#185FA5', fontSize: 11, fontWeight: 600 }}>
                      🖥 Browser abierto · Click para resaltar
                    </span>
                    <button
                      onClick={handleReInspect}
                      disabled={inspecting}
                      title="Vuelve a inspeccionar la página en la que está el navegador ahora mismo"
                      style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 6, border: '1px solid #3B8BD4', background: 'transparent', color: '#3B8BD4', fontSize: 11, fontWeight: 600, cursor: inspecting ? 'not-allowed' : 'pointer', opacity: inspecting ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                      ↻ Re-inspeccionar página actual
                    </button>
                  </span>
                )}
                {highlightedIdx !== null && (
                  <button onClick={handleClearHighlight}
                    style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #F47920', background: 'transparent', color: '#F47920', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
                    ✕ Limpiar highlight
                  </button>
                )}
                <button
                  onClick={() => { setShowImportPanel(v => !v); if (!showImportPanel) loadPomPages(); setImportResult(null); }}
                  style={{ padding: '3px 10px', borderRadius: 6, border: 'none', background: showImportPanel ? '#534AB7' : '#7F77DD', color: '#fff', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
                  {showImportPanel ? '✕ Cerrar POM' : '◈ Importar al POM'}
                </button>
                <input style={{ ...INPUT, width: 180 }} value={searchFilter} onChange={e => setSearchFilter(e.target.value)} placeholder="Filtrar por id, texto..." />
              </div>

              {/* Tag sub-filter chips */}
              {availableTags.length > 1 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                  <button onClick={() => setTagFilter('all')}
                    style={{ padding: '3px 10px', borderRadius: 20, border: `1px solid ${tagFilter === 'all' ? '#3B8BD4' : 'var(--color-border-secondary)'}`, background: tagFilter === 'all' ? '#3B8BD4' : 'transparent', color: tagFilter === 'all' ? '#fff' : 'var(--color-text-secondary)', fontSize: 11, cursor: 'pointer', fontWeight: tagFilter === 'all' ? 700 : 400 }}>
                    Todos ({inspectResult.elements.length})
                  </button>
                  {availableTags.map(tag => {
                    const count = inspectResult.elements.filter(el => el.tag === tag).length;
                    const active = tagFilter === tag;
                    return (
                      <button key={tag} onClick={() => setTagFilter(tag)}
                        style={{ padding: '3px 10px', borderRadius: 20, border: `1px solid ${active ? '#1B2E5E' : 'var(--color-border-secondary)'}`, background: active ? '#1B2E5E' : 'transparent', color: active ? '#B5D4F4' : 'var(--color-text-secondary)', fontSize: 11, cursor: 'pointer', fontWeight: active ? 700 : 400, fontFamily: 'monospace' }}>
                        &lt;{tag}&gt; {count}
                      </button>
                    );
                  })}
                </div>
              )}
              {/* POM Import Panel */}
              {showImportPanel && (
                <div style={{ padding: '12px 14px', borderRadius: 10, border: '1px solid #C4C0F5', background: '#F5F4FE', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#534AB7' }}>◈ Importar al POM</span>
                    <span style={{ fontSize: 11, color: '#534AB7' }}>{selectedForImport.size} seleccionado{selectedForImport.size !== 1 ? 's' : ''}</span>
                    <button onClick={selectAllVisible}
                      style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid #C4C0F5', background: 'transparent', color: '#534AB7', fontSize: 11, cursor: 'pointer' }}>
                      Seleccionar todos
                    </button>
                    <button onClick={() => setSelectedForImport(new Set())}
                      style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid #C4C0F5', background: 'transparent', color: '#534AB7', fontSize: 11, cursor: 'pointer' }}>
                      Limpiar
                    </button>
                    <div style={{ flex: 1 }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <select
                        value={selectedPageId}
                        onChange={e => setSelectedPageId(e.target.value)}
                        disabled={loadingPages}
                        style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #C4C0F5', background: '#fff', fontSize: 12, color: '#534AB7', cursor: 'pointer', minWidth: 160 }}>
                        {loadingPages
                          ? <option>Cargando...</option>
                          : pomPages.length === 0
                            ? <option value="">Sin pantallas POM</option>
                            : pomPages.map(sys => (
                                <optgroup key={sys.system_id} label={sys.system_name}>
                                  {sys.pages.map(p => (
                                    <option key={p.id} value={p.id}>{p.name}{p.url_pattern ? ` (${p.url_pattern})` : ''}</option>
                                  ))}
                                </optgroup>
                              ))
                        }
                      </select>
                      <button
                        onClick={handleImportToPOM}
                        disabled={selectedForImport.size === 0 || !selectedPageId || importing}
                        style={{ padding: '5px 14px', borderRadius: 6, border: 'none', background: (selectedForImport.size > 0 && selectedPageId) ? '#534AB7' : 'var(--color-background-secondary)', color: (selectedForImport.size > 0 && selectedPageId) ? '#fff' : 'var(--color-text-tertiary)', fontSize: 12, fontWeight: 600, cursor: (selectedForImport.size > 0 && selectedPageId) ? 'pointer' : 'not-allowed' }}>
                        {importing ? '⟳ Importando...' : `→ Importar ${selectedForImport.size}`}
                      </button>
                    </div>
                  </div>
                  {importResult && (
                    <div style={{ padding: '6px 10px', borderRadius: 6, background: '#E1F5EE', color: '#0F6E56', fontSize: 11 }}>
                      ✓ {importResult.created} objeto{importResult.created !== 1 ? 's' : ''} importado{importResult.created !== 1 ? 's' : ''}
                      {importResult.skipped > 0 ? ` · ${importResult.skipped} omitido${importResult.skipped !== 1 ? 's' : ''} (ya existían)` : ''}
                    </div>
                  )}
                </div>
              )}

              <div style={{ flex: 1, overflowY: 'auto' }}>
                {filtered.length === 0
                  ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-tertiary)' }}>Sin resultados</div>
                  : filtered.map((el, i) => (
                    <div key={`${el.tag}-${el.index}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                      {showImportPanel && (
                        <div onClick={() => toggleSelectForImport(el.index)}
                          style={{ marginTop: 14, width: 16, height: 16, borderRadius: 4, border: `2px solid ${selectedForImport.has(el.index) ? '#534AB7' : 'var(--color-border-secondary)'}`, background: selectedForImport.has(el.index) ? '#534AB7' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }}>
                          {selectedForImport.has(el.index) && <span style={{ color: '#fff', fontSize: 9 }}>✓</span>}
                        </div>
                      )}
                      <div style={{ flex: 1 }}>
                        <ElementCard
                          el={el}
                          index={i}
                          highlighted={highlightedIdx === el.index}
                          onHighlight={() => handleHighlight(el, currentUrl)}
                        />
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>
          )}

          {inspecting && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, color: 'var(--color-text-tertiary)' }}>
              <div style={{ fontSize: 32 }}>⟳</div>
              <div>Cargando página y extrayendo elementos...</div>
              <div style={{ fontSize: 11 }}>Esto puede tomar 5–15 segundos</div>
            </div>
          )}

          {!inspectResult && !inspecting && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, color: 'var(--color-text-tertiary)' }}>
              <div style={{ fontSize: 40 }}>🔍</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>Inspecciona cualquier página web</div>
              <div style={{ fontSize: 12, textAlign: 'center', maxWidth: 400 }}>
                Ingresa una URL y selecciona el tipo de elementos. El inspector cargará la página con Chrome y extraerá los elementos con sus propiedades y selectores sugeridos.
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── VALIDATE ── */}
      {mode === 'validate' && (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div style={{ display: 'flex', gap: 10, marginBottom: validateUrl.trim() && !isValidUrl(validateUrl) ? 4 : 10, flexWrap: 'wrap' }}>
            <input
              style={{ ...INPUT, flex: 1, border: validateUrl.trim() && !isValidUrl(validateUrl) ? '1px solid #F7C1C1' : INPUT.border }}
              value={validateUrl} onChange={e => { setValidateUrl(e.target.value); setError(''); }}
              placeholder="https://ejemplo.com/login" />
            {validateUrl.trim() && !isValidUrl(validateUrl) && (
              <div style={{ width: '100%', fontSize: 11, color: '#A32D2D', marginTop: -6, paddingLeft: 2 }}>
                La URL debe incluir el protocolo · ej: <strong>https://ejemplo.com/login</strong>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <select style={{ ...INPUT, width: 140 }} value={strategy} onChange={e => setStrategy(e.target.value)}>
              {STRATEGIES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <input style={{ ...INPUT, flex: 1 }} value={selector} onChange={e => setSelector(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleValidate()}
              placeholder={strategy === 'css' ? '#username, input[name="email"]' : strategy === 'xpath' ? '//button[@type="submit"]' : strategy === 'text' ? 'Iniciar sesión' : 'login-button'} />
            <button onClick={handleValidate} disabled={!isValidUrl(validateUrl) || !selector.trim() || validating}
              style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: (isValidUrl(validateUrl) && selector.trim()) ? '#3B8BD4' : 'var(--color-background-secondary)', color: (isValidUrl(validateUrl) && selector.trim()) ? '#fff' : 'var(--color-text-tertiary)', cursor: (isValidUrl(validateUrl) && selector.trim()) ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
              {validating ? '⟳ Validando...' : '✓ Validar'}
            </button>
          </div>

          {validateResult?.ok && (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <div style={{ padding: '14px 18px', borderRadius: 12, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 14, background: validateResult.found ? '#E1F5EE' : '#FCEBEB', border: `1px solid ${validateResult.found ? '#9DE3C7' : '#F7C1C1'}` }}>
                <div style={{ fontSize: 28 }}>{validateResult.found ? '✅' : '❌'}</div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: validateResult.found ? '#0F6E56' : '#A32D2D' }}>
                    {validateResult.found ? `${validateResult.count} elemento${validateResult.count !== 1 ? 's' : ''} encontrado${validateResult.count !== 1 ? 's' : ''}` : 'No encontrado'}
                  </div>
                  <div style={{ fontSize: 12, color: validateResult.found ? '#1D9E75' : '#C24B4A', marginTop: 3 }}>
                    <code>{selector}</code> — estrategia <strong>{strategy}</strong>
                  </div>
                </div>
              </div>
              {validateResult.elements.map((el, i) => <ElementCard key={i} el={el} index={i} highlighted={false} onHighlight={() => {}} />)}
            </div>
          )}

          {!validateResult && !validating && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, color: 'var(--color-text-tertiary)' }}>
              <div style={{ fontSize: 40 }}>✓</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>Valida si un selector existe</div>
              <div style={{ fontSize: 12, textAlign: 'center', maxWidth: 400 }}>
                Escribe la URL y el selector. Soporta CSS, XPath, texto visible, data-testid y ARIA roles.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}